import { useState, useEffect } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"
import { showToast } from "utils/swalToast"

// Services
import {
  fetchAsistenteById,
  createAsistente,
  updateAsistente,
  fetchSucursales,
  fetchDependencias,
  fetchTiposAsistente,
} from "./service"

function Add() {
  const [asistenteToEdit, setAsistenteToEdit] = useState(null)
  const navigate = useNavigate()
  const { id } = useParams()

  // Form data
  const [formData, setFormData] = useState({
    nombre: "",
    documentNumber: "",
    dependenciaId: "",
    tipoAsistenteId: "",
  })

  // Dropdown data
  const [sucursales, setSucursales] = useState([])
  const [dependencias, setDependencias] = useState([])
  const [tiposAsistente, setTiposAsistente] = useState([])
  const [selectedSucursal, setSelectedSucursal] = useState("")

  // Load sucursales on mount
  useEffect(() => {
    loadSucursales()
  }, [])

  // Load asistente data if editing
  useEffect(() => {
    if (id) {
      fetchAsistenteById(id)
        .then(async (asistente) => {
          setAsistenteToEdit(asistente)

          // Load dependencias and tipos based on existing data
          if (asistente.dependenciaId) {
            // Find sucursal for this dependencia and load tipos
            await loadDependenciasForEdit(asistente.dependenciaId, asistente.tipoAsistenteId)
          }

          // Set form data AFTER loading related data to avoid triggering useEffect
          setFormData({
            nombre: asistente.nombre || "",
            documentNumber: asistente.documentNumber || "",
            dependenciaId: asistente.dependenciaId || "",
            tipoAsistenteId: asistente.tipoAsistenteId || "",
          })
        })
        .catch((error) => {
          console.error("Error loading asistente:", error)
          showToast({
            icon: "error",
            title: "Error",
            text: "No se pudo cargar el asistente",
          })
          navigate("/asistencia")
        })
    }
  }, [id])

  // Load dependencias when sucursal changes
  useEffect(() => {
    if (selectedSucursal) {
      loadDependencias()
    } else {
      setDependencias([])
      setFormData((prev) => ({ ...prev, dependenciaId: "" }))
    }
  }, [selectedSucursal])

  // Load tipos when dependencia changes
  useEffect(() => {
    if (formData.dependenciaId) {
      loadTiposAsistente()
    } else {
      setTiposAsistente([])
      setFormData((prev) => ({ ...prev, tipoAsistenteId: "" }))
    }
  }, [formData.dependenciaId])

  const loadSucursales = async () => {
    try {
      const data = await fetchSucursales()
      setSucursales(data || [])
    } catch (error) {
      console.error("Error loading sucursales:", error)
    }
  }

  const loadDependencias = async () => {
    try {
      const data = await fetchDependencias(selectedSucursal)
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading dependencias:", error)
    }
  }

  const loadDependenciasForEdit = async (dependenciaId, tipoAsistenteId) => {
    try {
      // Fetch the specific dependencia by ID to get its sucursal
      const fetchDependenciaById = async (id) => {
        const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
        const response = await fetch(`${require("config").default.api.baseURL}/dependencias-asistencia/${id}`, {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        if (!response?.ok) {
          throw new Error("Error al obtener la dependencia")
        }
        return response.json()
      }

      const dep = await fetchDependenciaById(dependenciaId)

      if (dep && dep.sucursalId) {
        // Set the sucursal first
        setSelectedSucursal(dep.sucursalId)

        // Load dependencias for this sucursal
        const dependenciasData = await fetchDependencias(dep.sucursalId)
        setDependencias(dependenciasData || [])

        // Load tipos de asistente for this dependencia
        await loadTiposAsistenteForEdit(dependenciaId)
      }
    } catch (error) {
      console.error("Error loading dependencias for edit:", error)
    }
  }

  const loadTiposAsistente = async () => {
    try {
      const data = await fetchTiposAsistente(formData.dependenciaId)
      setTiposAsistente(data || [])
    } catch (error) {
      console.error("Error loading tipos de asistente:", error)
    }
  }

  const loadTiposAsistenteForEdit = async (dependenciaId) => {
    try {
      console.log("Loading tipos for dependenciaId:", dependenciaId)
      const data = await fetchTiposAsistente(dependenciaId)
      console.log("Tipos de asistente cargados:", data)
      setTiposAsistente(data || [])
    } catch (error) {
      console.error("Error loading tipos de asistente for edit:", error)
    }
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validations
    if (!formData.nombre || formData.nombre.trim() === "") {
      showToast({
        icon: "warning",
        title: "Campo requerido",
        text: "El nombre es requerido",
      })
      return
    }

    if (!formData.documentNumber || formData.documentNumber.trim() === "") {
      showToast({
        icon: "warning",
        title: "Campo requerido",
        text: "El número de documento es requerido",
      })
      return
    }

    if (!formData.dependenciaId) {
      showToast({
        icon: "warning",
        title: "Campo requerido",
        text: "La dependencia es requerida",
      })
      return
    }

    if (!formData.tipoAsistenteId) {
      showToast({
        icon: "warning",
        title: "Campo requerido",
        text: "El tipo de asistente es requerido",
      })
      return
    }

    const data = {
      nombre: formData.nombre,
      documentNumber: formData.documentNumber,
      dependenciaId: formData.dependenciaId,
      tipoAsistenteId: formData.tipoAsistenteId,
    }

    try {
      if (asistenteToEdit) {
        await updateAsistente(id, data)
      } else {
        await createAsistente(data)
      }
      navigate("/asistencia")
    } catch (error) {
      console.error("Error en la operación:", error)
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h5">
                  {asistenteToEdit ? "Editar asistente" : "Agregar asistente"}
                </MDTypography>
              </MDBox>
              <MDBox pt={2} px={2} component="form" role="form" onSubmit={handleSubmit}>
                {/* Nombre */}
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    name="nombre"
                    label="Nombre completo"
                    fullWidth
                    required
                    value={formData.nombre}
                    onChange={handleChange}
                  />
                </MDBox>

                {/* Documento */}
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    name="documentNumber"
                    label="Número de documento"
                    fullWidth
                    required
                    value={formData.documentNumber}
                    onChange={handleChange}
                  />
                </MDBox>

                {/* Sucursal */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Sucursal</InputLabel>
                    <Select
                      label="Sucursal"
                      value={selectedSucursal}
                      onChange={(e) => setSelectedSucursal(e.target.value)}
                      required
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione una sucursal</MenuItem>
                      {sucursales.map((suc) => (
                        <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                          {suc.descripcion}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Dependencia */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Dependencia</InputLabel>
                    <Select
                      label="Dependencia"
                      name="dependenciaId"
                      value={formData.dependenciaId}
                      onChange={handleChange}
                      required
                      disabled={!selectedSucursal}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione una dependencia</MenuItem>
                      {dependencias.map((dep) => (
                        <MenuItem key={dep.id} value={dep.id}>
                          {dep.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Tipo de Asistente */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Asistente</InputLabel>
                    <Select
                      label="Tipo de Asistente"
                      name="tipoAsistenteId"
                      value={formData.tipoAsistenteId}
                      onChange={handleChange}
                      required
                      disabled={!formData.dependenciaId}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione un tipo</MenuItem>
                      {tiposAsistente.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Botones */}
                <MDBox mb={2} sx={{ textAlign: "center" }}>
                  <Link to="/asistencia">
                    <MDButton variant="outlined" color="info" sx={{ mr: 1, width: 90 }}>
                      Cancelar
                    </MDButton>
                  </Link>
                  <MDButton type="submit" variant="gradient" color="info" sx={{ ml: 1, width: 90 }}>
                    {asistenteToEdit ? "Actualizar" : "Agregar"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default Add
